import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Plus, Save, RefreshCw } from 'lucide-react'
import { washCatalogService } from '@/services/washCatalogService'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

function slugifyId(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

export default function WashCatalogPage() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [packages, setPackages] = useState([])

  useEffect(() => {
    ;(async () => {
      try {
        setLoading(true)
        await washCatalogService.ensureInitialized()
        const doc = await washCatalogService.get()
        setPackages(doc?.packages || [])
      } catch (e) {
        console.error(e)
        toast.error('Failed to load wash catalog')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const tierTemplate = useMemo(() => {
    const first = packages[0]?.tiers
    if (Array.isArray(first) && first.length) return first.map((t) => ({ id: t.id, label: t.label }))
    return [
      { id: 'car', label: 'Cars' },
      { id: 'suv', label: 'SUV / D-Cabs' },
      { id: 'kombi', label: 'Kombi' },
    ]
  }, [packages])

  const addService = () => {
    const next = {
      id: `svc_${Date.now()}`,
      name: '',
      description: '',
      tiers: tierTemplate.map((t) => ({ ...t, price: 0, poa: false })),
    }
    setPackages((p) => [next, ...p])
  }

  const save = async () => {
    try {
      setSaving(true)
      // Ensure IDs are stable
      const normalized = packages.map((p) => ({
        ...p,
        id: p.id && !String(p.id).startsWith('svc_') ? p.id : slugifyId(p.name) || p.id,
      }))
      await washCatalogService.updatePackages(normalized)
      setPackages(normalized)
      toast.success('Wash catalog saved')
    } catch (e) {
      console.error(e)
      toast.error('Could not save catalog')
    } finally {
      setSaving(false)
    }
  }

  const reload = async () => {
    try {
      setLoading(true)
      const doc = await washCatalogService.get()
      setPackages(doc?.packages || [])
    } catch (e) {
      console.error(e)
      toast.error('Failed to reload')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10">
        <p className="text-sm text-muted-foreground">Loading wash catalog…</p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-8">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Wash catalog</h1>
          <p className="text-sm text-muted-foreground">
            Add, edit, and price services. Changes reflect in Scan and the customer price list.
          </p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={reload}>
            <RefreshCw className="size-4" />
            Reload
          </Button>
          <Button type="button" onClick={save} disabled={saving}>
            <Save className="size-4" />
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      <Card className="border-border/80 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Services</CardTitle>
            <CardDescription>Service name, description, tier prices, or POA (price on arrival).</CardDescription>
          </div>
          <Button type="button" variant="secondary" onClick={addService}>
            <Plus className="size-4" />
            Add service
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {packages.length === 0 ? (
            <p className="text-sm text-muted-foreground">No services yet.</p>
          ) : (
            packages.map((svc, idx) => (
              <div key={`${svc.id}-${idx}`} className="rounded-xl border border-border/80 p-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Service name</Label>
                    <Input
                      value={svc.name}
                      onChange={(e) => {
                        const v = e.target.value
                        setPackages((prev) =>
                          prev.map((p) => (p === svc ? { ...p, name: v } : p))
                        )
                      }}
                      placeholder="e.g. Wash & Go"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={svc.description || ''}
                      onChange={(e) => {
                        const v = e.target.value
                        setPackages((prev) =>
                          prev.map((p) => (p === svc ? { ...p, description: v } : p))
                        )
                      }}
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <Separator className="my-4" />

                <div className="grid gap-3 sm:grid-cols-3">
                  {svc.tiers.map((t) => (
                    <div key={t.id} className="space-y-2 rounded-lg border border-border/60 p-3">
                      <Label className={cn('text-xs text-muted-foreground')}>{t.label}</Label>
                      <label className="flex items-center gap-2 text-xs cursor-pointer">
                        <input
                          type="checkbox"
                          checked={Boolean(t.poa)}
                          onChange={(e) => {
                            const poa = e.target.checked
                            setPackages((prev) =>
                              prev.map((p) => {
                                if (p !== svc) return p
                                return {
                                  ...p,
                                  tiers: p.tiers.map((x) =>
                                    x.id === t.id ? { ...x, poa, price: poa ? 0 : x.price } : x
                                  ),
                                }
                              })
                            )
                          }}
                        />
                        <span>POA (price on arrival)</span>
                      </label>
                      <Input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        disabled={Boolean(t.poa)}
                        placeholder={t.poa ? 'POA' : '0.00'}
                        value={t.poa ? '' : Number(t.price || 0)}
                        onChange={(e) => {
                          const nextPrice = Number(e.target.value || 0)
                          setPackages((prev) =>
                            prev.map((p) => {
                              if (p !== svc) return p
                              return {
                                ...p,
                                tiers: p.tiers.map((x) =>
                                  x.id === t.id ? { ...x, price: nextPrice, poa: false } : x
                                ),
                              }
                            })
                          )
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  )
}

